import { ChevronDown, ChevronRight, Infinity as InfinityIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import { CompanyMonogram, CompanySpine } from '@/shared/components/common'
import { RejectDropZone } from '@/shared/components/dnd'
import { usePersistedFlag } from '@/shared/hooks/use-persisted-flag'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { ProjectSummary } from '@/shared/types/domain'

export const SWIMLANE_ZONE_ID = 'swimlane-permanentes'
export const SWIMLANE_STORAGE_KEY = 'pm-board-swimlane-open'

/**
 * El motivo del rechazo viaja como CLAVE, no como texto: quien la muestra
 * (la zona o el toast del tablero) es el que la traduce, con el idioma de la
 * persona que está mirando.
 */
export const SWIMLANE_REJECT_KEY = 'board.ongoing.rejectReason'

export interface OngoingSwimlaneProps {
  projects: readonly ProjectSummary[]
  className?: string
}

/**
 * La swimlane fija de permanentes, arriba de las columnas.
 *
 * **Colapsada por default y recordada**: están siempre y casi nunca son la
 * pregunta del día; ocupando media pantalla todos los días serían ruido, pero
 * escondidos del todo se olvida que existen. Chips horizontales, no tarjetas:
 * la información que importa de un permanente es que está, de quién es y poco
 * más.
 *
 * No acepta drops. Un proyecto con entrega que cae acá se rechaza con el motivo
 * a la vista (plan 2.8: el ranking de los permanentes es un scope aparte).
 */
export function OngoingSwimlane({ projects, className }: OngoingSwimlaneProps) {
  const t = useT()
  const [open, , toggle] = usePersistedFlag(SWIMLANE_STORAGE_KEY, false)

  return (
    <RejectDropZone
      id={SWIMLANE_ZONE_ID}
      reason={t(SWIMLANE_REJECT_KEY)}
      className={cn('hairline mb-3 bg-surface-1 p-2', className)}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-1 py-1 text-2xs text-text-2 hover:text-text-1"
      >
        {open ? (
          <ChevronDown size={13} aria-hidden="true" />
        ) : (
          <ChevronRight size={13} aria-hidden="true" />
        )}
        <InfinityIcon size={13} aria-hidden="true" className="text-phase-ongoing" />
        {t('board.ongoing.title')}
        <span
          className="num text-text-3"
          aria-label={t('board.ongoing.count', { count: projects.length })}
        >
          {projects.length}
        </span>
        <span className="ml-auto text-text-3">{t('board.ongoing.hint')}</span>
      </button>

      {open ? (
        <div className="flex flex-wrap items-center gap-2 px-1 pt-2">
          {projects.length === 0 ? (
            <p className="text-2xs text-text-3">{t('board.ongoing.empty')}</p>
          ) : (
            projects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="hairline flex items-center gap-1.5 rounded-[var(--radius-control)] bg-surface-2/60 px-2 py-1 text-2xs text-text-1 hover:text-signal"
              >
                <CompanySpine
                  hue={project.company.color_hue}
                  label={project.company.name}
                  className="h-4"
                />
                <CompanyMonogram name={project.company.name} hue={project.company.color_hue} />
                <span className="truncate">{project.name}</span>
                {project.overdue_tasks > 0 ? (
                  <span
                    className="num text-overdue"
                    aria-label={t('board.card.overdueTasks', { count: project.overdue_tasks })}
                  >
                    <span aria-hidden="true">! </span>
                    {project.overdue_tasks}
                  </span>
                ) : null}
              </Link>
            ))
          )}
        </div>
      ) : null}
    </RejectDropZone>
  )
}
